import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useSpring } from 'framer-motion';
import { theme } from '../theme';

const ProgressBar = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${theme.gradients.primary};
  transform-origin: 0%;
  z-index: 1000;
  box-shadow: 0 0 10px rgba(124, 77, 255, 0.5);
`;

const ScrollIndicator = styled.div`
  position: fixed;
  right: ${theme.spacing.lg};
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Dot = styled(motion.div)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.active ? theme.gradients.primary : 'rgba(124, 77, 255, 0.2)'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: scale(1.3);
  }

  &::after {
    content: '${props => props.label}';
    position: absolute;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
    font-size: 0.75rem;
    color: ${theme.colors.textDark};
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    background: white;
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: ${theme.shadows.small};
  }

  &:hover::after {
    opacity: 1;
  }
`;

const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const [activeSection, setActiveSection] = useState(0);

  const sections = [
    { id: 'hero', label: 'Home' },
    { id: 'features', label: 'Features' },
    { id: 'process', label: 'Process' },
    { id: 'dataflow', label: 'Data Flow' },
    { id: 'metrics', label: 'Metrics' },
    { id: 'impact', label: 'Impact' },
    { id: 'cta', label: 'Get Started' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      const sections = document.querySelectorAll('section');
      
      sections.forEach((section, index) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (index) => {
    const sections = document.querySelectorAll('section');
    if (sections[index]) {
      sections[index].scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <ProgressBar style={{ scaleX }} />
      <ScrollIndicator>
        {sections.map((section, index) => (
          <Dot
            key={section.id}
            active={activeSection === index}
            label={section.label}
            onClick={() => scrollToSection(index)}
            whileHover={{ scale: 1.3 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </ScrollIndicator>
    </>
  );
};

export default ScrollProgress;
